import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from 'src/components/ui/Icon/Icon';
import Divider from 'src/components/ui/Divider/Divider';
import Input from 'src/components/ui/Input/Input';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import useLogin from 'src/services/loginService';
import { useTheme } from 'src/providers/ThemeProvider';
import MaintenanceBanner from 'src/components/layouts/MaintenanceBanner/MaintenanceBanner';
import styles from './styles/Login.module.scss';

function Login() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { handleSubmit } = useLogin();

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(false);
    const { name, value } = e.target;
    if (name === 'username') {
      setEmail(value);
    } else if (name === 'password') {
      setPassword(value);
    }
  };

  const handleLogin = async (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    if (email && password) {
      await handleSubmit(email, password);
    } else {
      setError(true);
    }
  };

  const validUserEmail = error && !!email;

  return (
    <div className={styles.page}>
      <MaintenanceBanner context="login" />
      <div className={styles.root}>
        <div className={styles.card}>
          <Icon name={theme === 'dark' ? 'logo-full-white' : 'logo-full-dark'} width={91} height={30} />
          <h1 className="typo-title-36 text-full mt-32">{t('login.title')}</h1>
          <p className="typo-text-16 text-faded mt-8 mb-32">{t('login.subtitle')}</p>
          <form onSubmit={handleLogin} className="flex flex-col">
            <Input
              label={t('general.email')}
              className={styles.input}
              placeholder={t('login.email_placeholder')}
              onChange={handleInputChange}
              value={email}
              name="username"
              autoComplete="username"
              error={validUserEmail}
              errorMessage={t('input.invalid_email')}
            />
            <div className="mt-20">
              <Input
                label={t('onboarding.password')}
                className={styles.input}
                name="password"
                placeholder={t('login.password_placeholder')}
                type="password"
                autoComplete="current-password"
                onChange={handleInputChange}
                value={password}
                secret
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
              />
            </div>
            <Button
              className={`${styles.submit} mt-24`}
              type="submit"
              disabled={!email || !password}
              title={t('login.submit')}
              tone={ButtonToneProps.PRIMARY_BLUE}
              onClick={handleLogin}
            />
          </form>
          <Divider customColor="border-subtle" className="mt-32 mb-24" />
          <p className="typo-text-14 text-faded text-center">
            {t('login.no_account')}{' '}
            <Link to="/register" className={styles.link}>
              {t('login.register_now')}
            </Link>
          </p>
        </div>
        <div className={`${styles.footer} typo-text-12`}>
          <span>{t('login.copyright', { year: new Date().getFullYear() })}</span>
        </div>
      </div>
    </div>
  );
}

export default Login;
